import { randomUUID } from '@random';

import { PermissionModel } from './authorization';
import { MediaitemModel } from './mediaitems';
import { Unit } from './shared';
import { TagModel } from './tags';
import { UserModel } from './users';
import { VersionedEntityOperationType, VersionedEntityType } from './versions';

enum ContentItemModality {
  TEXT = 'TEXT',
  MEDIA = 'MEDIA',
  CODE = 'CODE',
  GENERATED_TEXT = 'GENERATED_TEXT',
  GENERATED_SPEECH = 'GENERATED_SPEECH',
  GENERATED_IMAGE = 'GENERATED_IMAGE'
}

enum BlockType {
  GROUP = 'GROUP',

  STATIC = 'STATIC',

  // TIMER
  INPUT_TIMER = 'INPUT_TIMER',

  // NUMERICAL
  INPUT_BOOLEAN = 'INPUT_BOOLEAN',
  INPUT_INTEGER = 'INPUT_INTEGER',
  INPUT_FLOAT = 'INPUT_FLOAT',
  INPUT_RATING = 'INPUT_RATING',
  INPUT_RANGE = 'INPUT_RANGE',

  // DATE / TIME
  INPUT_DATE = 'INPUT_DATE',
  INPUT_TIME = 'INPUT_TIME',
  INPUT_DATETIME = 'INPUT_DATETIME',

  // TEXT
  INPUT_TEXT = 'INPUT_TEXT',
  INPUT_MULTIPLE_CHOICE = 'INPUT_MULTIPLE_CHOICE',
  INPUT_SINGLE_CHOICE = 'INPUT_SINGLE_CHOICE',

  // GAMES
  GAME_BRICKS = 'GAME_BRICKS',
  GAME_BLANKS = 'GAME_BLANKS',
  GAME_FLASHCARDS = 'GAME_FLASHCARDS',
  GAME_CONNECTOR = 'GAME_CONNECTOR',

  GAME_PUZZLE = 'GAME_PUZZLE',

  // MEASUREMENTS
  BLOOD_TEST = 'BLOOD_TEST',
  GROWTH_CHART = 'GROWTH_CHART',

  // TRAINING
  INPUT_TRAINING_RUNNING = 'INPUT_TRAINING_RUNNING',
  INPUT_TRAINING_CYCLING = 'INPUT_TRAINING_CYCLING',
  INPUT_TRAINING_LAP_SWIMMING = 'INPUT_TRAINING_LAP_SWIMMING',
  INPUT_TRAINING_STRENGTH = 'INPUT_TRAINING_STRENGTH',
  INPUT_TRAINING_CARDIO = 'INPUT_TRAINING_CARDIO',
  INPUT_TRAINING_YOGA = 'INPUT_TRAINING_YOGA',
  INPUT_TRAINING_PILATES = 'INPUT_TRAINING_PILATES',

  // FILE UPLOAD
  INPUT_FILE_UPLOAD = 'INPUT_FILE_UPLOAD'
}

enum BlockCategory {
  GROUP = 'GROUP',
  BASIC = 'BASIC',
  ACTIVE = 'ACTIVE',
  GAMES = 'GAMES',
  MEASUREMENTS = 'MEASUREMENTS'
}

enum BlockBooleanType {
  YES_NO = 'YES_NO',
  TRUE_FALSE = 'TRUE_FALSE'
}

enum BlockGroupType {
  REPETITIONS = 'REPETITIONS',
  TIME = 'TIME'
}

enum BlockGroupUnit {
  REPETITIONS = 'REPETITIONS',
  SECOND = 'SECOND'
}

enum BlockIntensity {
  REST = 'REST',
  WARMUP = 'WARMUP',
  COOLDOWN = 'COOLDOWN',
  RECOVERY = 'RECOVERY',
  INTERVAL = 'INTERVAL',
  NORMAL = 'NORMAL'
}

enum BlockAssertionType {
  ASSERTION = 'ASSERTION',
  DURATION_TIME = 'DURATION_TIME',
  DURATION_DISTANCE = 'DURATION_DISTANCE',
  DURATION_SWIM_DISTANCE = 'DURATION_SWIM_DISTANCE',
  DURATION_OPEN = 'DURATION_OPEN',
  DURATION_CALORIES = 'DURATION_CALORIES',
  DURATION_HEART_RATE = 'DURATION_HEART_RATE',
  DURATION_POWER = 'DURATION_POWER',
  DURATION_REPS = 'DURATION_REPS',
  TARGET_PACE = 'TARGET_PACE',
  TARGET_RUNNING_CADENCE = 'TARGET_RUNNING_CADENCE',
  TARGET_HEART_RATE = 'TARGET_HEART_RATE',
  TARGET_POWER = 'TARGET_POWER',
  TARGET_SPEED = 'TARGET_SPEED',
  FILE_UPLOAD_COMPLETION = 'FILE_UPLOAD_COMPLETION',
  CRITERIA = 'CRITERIA'
}

enum BlockAssertionValueType {
  BOOLEAN = 'BOOLEAN',
  TIME = 'TIME',
  INTEGER = 'INTEGER',
  FLOAT = 'FLOAT',
  STRING = 'STRING',
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  PACE = 'PACE',
  ARRAY_OF_STRINGS = 'ARRAY_OF_STRINGS',
  DICT_OF_STRINGS = 'DICT_OF_STRINGS',
  FILE_UPLOAD_COMPLETION = 'FILE_UPLOAD_COMPLETION',
  BLANKS = 'BLANKS',
  INPUT_TEXT_CRITERIA_LIST = 'INPUT_TEXT_CRITERIA_LIST'
}

enum BlockAssertionCompareType {
  /*
   * SINGLE VALUE COMPARISONS
   */

  // exact equality
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',

  // inequalities
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  GREATER_THAN_OR_EQUALS = 'GREATER_THAN_OR_EQUALS',
  LESS_THAN_OR_EQUALS = 'LESS_THAN_OR_EQUALS',

  // range comparison
  BETWEEN = 'BETWEEN',

  /*
   * MULTIPLE VALUE (ARRAYS) COMPARISONS
   */
  // unordered equality - same items, before comparison may be in different order
  UNORDERED_EQUALS = 'UNORDERED_EQUALS',
  NOT_UNORDERED_EQUALS = 'NOT_UNORDERED_EQUALS',

  // array comparisons
  CONTAINS = 'CONTAINS',

  /*
   * AI EVALUATION (LLM-based, no static comparison)
   */
  AGENT_GRADED = 'AGENT_GRADED'
}

enum BlockAssertionStepType {
  POOL_LENGTH = 'POOL_LENGTH'
}

enum BlockCriterionType {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL'
}

interface BlockCriterion {
  cid: string;
  title: string;
  description: string;
  type: BlockCriterionType;
}

interface BlockAssertionSpecConstraintModel {
  unit: Unit | null;
  minValue: number | null;
  maxValue: number | null;
  defaultMinValue: number | null;
  defaultMaxValue: number | null;
  defaultValue: number | null;
  step: number | null;
}

interface BlockAssertionSpecModel {
  type: BlockAssertionType;
  defaultForBlockTypes: BlockType[];
  allowedForBlockTypes: BlockType[];

  valueType: BlockAssertionValueType;
  compareTypes: BlockAssertionCompareType[] | null;
  stepType: BlockAssertionStepType | null;

  constraints: BlockAssertionSpecConstraintModel[];
}

interface BlockAssertionValueModel {
  exact:
    | boolean
    | number
    | string
    | number[]
    | object[]
    | string[]
    | object
    | Record<string, string>
    | null;
  min: boolean | number | string | number[] | object[] | string[] | object | null;
  max: boolean | number | string | number[] | object[] | string[] | object | null;
  criteria?: BlockCriterion[];
}

interface BlockAssertionModel {
  cid: string | null;
  // generic sub-item target; None = block-level
  itemCid: string | null;
  type: BlockAssertionType;

  passMessage: string | null;
  failMessage: string | null;

  // how to score?
  countToScore: boolean | null;
  scoreWeight: number | null;

  // how to evaluate?
  compareType: BlockAssertionCompareType;
  valueType: BlockAssertionValueType;
  stepType: BlockAssertionStepType | null;
  value: BlockAssertionValueModel;
  step: number | null;
  unit: Unit | null;
}

interface ContentItemModel {
  // -- text part
  text: string | null;

  // -- mediaitem part
  mediaitemId: number | null;
  mediaitem: MediaitemModel | null;
  caption: string | null;
}
interface BaseWithContentItem {
  item: ContentItemModel;
}

interface BlockGroupContentModel {
  value: number;
  type: BlockGroupType;
  unit: BlockGroupUnit;
}

interface BlockStaticAttachmentModel {
  mediaitemId: number;
  text: string | null;
  originalFilename: string | null;
  mediaitem?: MediaitemModel | null;
  caption?: string | null;
}

interface BlockStaticContentModel extends BaseWithContentItem {
  attachments: BlockStaticAttachmentModel[];
}

interface BlockInputTimerStepModel extends BaseWithContentItem {
  stepTime: number;
}

interface BlockInputTimerContentModel extends BaseWithContentItem {
  finalTime: number | null;
  steps: BlockInputTimerStepModel[] | null;
}

interface BlockInputBooleanContentModel extends BaseWithContentItem {
  booleanType: BlockBooleanType | null;
}

interface BlockInputIntegerContentModel extends BaseWithContentItem {
  minValue: number | null;
  maxValue: number | null;
  unit: Unit | null;
}

interface BlockInputRatingContentModel extends BaseWithContentItem {
  minValue: number | null;
  maxValue: number | null;
  minValueText: string | null;
  maxValueText: string | null;
}

interface BlockInputFloatContentModel extends BaseWithContentItem {
  minValue: number | null;
  maxValue: number | null;
  precision: number | null;
  unit: Unit | null;
}

interface BlockInputDateContentModel extends BaseWithContentItem {
  minValue: string | null;
  maxValue: string | null;
}

interface BlockInputDatetimeContentModel extends BaseWithContentItem {
  minValue: string | null;
  maxValue: string | null;
}

interface BlockInputTimeContentModel extends BaseWithContentItem {
  minValue: string | null;
  maxValue: string | null;
}

interface BlockInputTextContentModel extends BaseWithContentItem {
  minLength: number | null;
  maxLength: number | null;
}

interface BlockContentChoiceModel extends BaseWithContentItem {
  cid: string | null;
}

interface BlockBrickModel extends BaseWithContentItem {
  cid: string | null;
}

interface BlockInputChoicesContentModel extends BaseWithContentItem {
  choices: BlockContentChoiceModel[] | null;
}

// Per BE FlashCardItem(BaseWithContentItem) — just cid + inherited item.
// Question = item.text; media = item.mediaitemId/mediaitem (inherited).
interface FlashCardItem extends BaseWithContentItem {
  cid: string;
}

interface BlockInputFlashcardsContentModel extends BaseWithContentItem {
  flashcards: FlashCardItem[];
  limitAnswerTime: boolean;
}

interface BlockBrickCategoryModel {
  cid: string;
  name: string;
}

interface BlankContentModel extends BaseWithContentItem {
  problems: BlankProblemModel[] | null;
}

interface BlankProblemModel extends BaseWithContentItem {
  cid: string;
  blanks: BlankModel[];
}

interface BlankModel {
  cid: string;
  text: string;
  explanation: string;
  occurrences: BlankOccurrenceModel[];
}

interface BlankOccurrenceModel {
  cid: string;
  start: number;
  end: number;
  isActive: boolean;
}

interface BlockInputBricksContentModel extends BaseWithContentItem {
  bricks: BlockBrickModel[] | null;
  categories: BlockBrickCategoryModel[] | null;
}

type CategoryToBricksAssociationsType = { [categoryCid: string]: string[] };

const BlockBricksDragTypes = {
  BRICK: 'brick'
} as const;

// Drag item interface for brick drag and drop operations
interface DraggedBrickModel {
  type: string;
  brick: BlockBrickModel;
  sourceCategoryCid: string;
  sourceIndex: number;
}

const BlockBlanksDragTypes = {
  BLANK: 'blank'
} as const;

interface DraggedBlankModel {
  type: string;
  blank: BlankModel;
  sourceIndex: number;
  sourceProblemCid: string;
}

interface BlockInputTrainingContentModel extends BaseWithContentItem {}

type FileUploadIdToMimeTypeAssociationType = { [fileUploadId: string]: string };

interface BlockInputFileUploadContentModel extends BaseWithContentItem {
  fileUploads: {
    id: string;
    instructions: string | null;
    allowedMimeType: string;
  }[];
}

interface BlockGenericModel<TContent> {
  // version & identity
  entityId: string | null;
  versionId: string | null;
  versionType: VersionedEntityType;
  operationType: VersionedEntityOperationType;
  createdAt: string;
  updatedAt: string;

  // ordering
  nextEntityId: string | null;
  groupEntityId: string | null;
  groupBlocks: BlockModel[] | null;

  type: BlockType;
  intensity: BlockIntensity;
  content: TContent;
  assertions: BlockAssertionModel[];
  tags: TagModel[];
}

interface BlockCreateModel {
  nextEntityId?: string | null;
  groupEntityId?: string | null;
  groupBlocks?: BlockCreateModel[];
  type?: BlockType;
  intensity?: BlockIntensity;
  content?:
    | BlockGroupContentModel
    | BlockStaticContentModel
    | BlockInputTimerContentModel
    | BlockInputBooleanContentModel
    | BlockInputIntegerContentModel
    | BlockInputRatingContentModel
    | BlockInputFloatContentModel
    | BlockInputDateContentModel
    | BlockInputDatetimeContentModel
    | BlockInputTimeContentModel
    | BlockInputTextContentModel
    | BlockInputChoicesContentModel
    | BlockInputBricksContentModel
    | BlockInputTrainingContentModel
    | BlockInputFileUploadContentModel;
  assertions?: BlockAssertionModel[];
  orderedTagEntityIds?: string[];
}

interface BlockUpdateModel {
  nextEntityId?: string | null;
  groupEntityId?: string | null;
  type?: BlockType;
  intensity?: BlockIntensity;
  content?:
    | BlockGroupContentModel
    | BlockStaticContentModel
    | BlockInputTimerContentModel
    | BlockInputBooleanContentModel
    | BlockInputIntegerContentModel
    | BlockInputRatingContentModel
    | BlockInputFloatContentModel
    | BlockInputDateContentModel
    | BlockInputDatetimeContentModel
    | BlockInputTimeContentModel
    | BlockInputTextContentModel
    | BlockInputChoicesContentModel
    | BlockInputTrainingContentModel
    | BlockInputFileUploadContentModel;
  assertions?: BlockAssertionModel[];
  orderedTagEntityIds?: string[];
}

type BlockContentModel =
  | BlockGroupContentModel
  | BlockStaticContentModel
  | BlockInputTimerContentModel
  | BlockInputBooleanContentModel
  | BlockInputIntegerContentModel
  | BlockInputRatingContentModel
  | BlockInputFloatContentModel
  | BlockInputDateContentModel
  | BlockInputDatetimeContentModel
  | BlockInputTimeContentModel
  | BlockInputTextContentModel
  | BlockInputChoicesContentModel
  | BlockInputTrainingContentModel
  | BlockInputFlashcardsContentModel
  | BlankContentModel
  | BlockInputBricksContentModel
  | BlockInputFileUploadContentModel;

interface BlockModel {
  // version & identity
  entityId: string;
  versionType: VersionedEntityType;
  operationType: VersionedEntityOperationType;
  createdAt: string;
  updatedAt: string;

  // ordering
  nextEntityId: string | null;
  groupEntityId: string | null;
  groupBlocks: BlockModel[] | null;

  // own fields
  type: BlockType;
  intensity: BlockIntensity;
  content: BlockContentModel;
  assertions?: BlockAssertionModel[];
  orderedTagEntityIds?: string[];
  tags: TagModel[];
}

interface TaskCreateModel {
  title?: string;
  durationSeconds?: number;
  blocks?: BlockCreateModel[];
  tagEntityIds?: string[];
}

interface TaskUpdateModel {
  title?: string;
  durationSeconds?: number;
  tagEntityIds?: string[];
}

interface TaskModel {
  // version & identity
  entityId: string | null;
  versionType: VersionedEntityType;
  operationType: VersionedEntityOperationType;
  derivativeOperationType?: VersionedEntityOperationType | null;
  createdAt: string;
  updatedAt: string;

  // own fields
  title: string;
  durationSeconds?: number;
  blocks?: BlockModel[];
  tags?: TagModel[];
  user?: UserModel;
  __permissions?: PermissionModel[];
}

interface TaskValidationSummaryModel {
  errorsCount: number;
  warningsCount: number;
}

/*
 * EXERCISES
 */
interface BlockExerciseModel {
  id: number;
  type: string;
  categoryType: string;
  subcategoryType: string;
  typeName: string;
  categoryTypeName: string;
  subcategoryTypeName: string;
  allowedForBlockTypes: BlockType[];
}

const isAsyncCompareType = (ct: BlockAssertionCompareType): boolean =>
  ct === BlockAssertionCompareType.AGENT_GRADED;

const hasAsyncAssertion = (assertions: BlockAssertionModel[] | undefined): boolean =>
  (assertions ?? []).some((a) => isAsyncCompareType(a.compareType));

// Per BE D8 / §5.1 pkt 6: item-targeted asercje NIE mają countToScore/scoreWeight.
const makeDefaultAssertionForCard = (itemCid: string | null): BlockAssertionModel => ({
  cid: null,
  itemCid,
  type: BlockAssertionType.ASSERTION,
  passMessage: null,
  failMessage: null,
  valueType: BlockAssertionValueType.STRING,
  compareType: BlockAssertionCompareType.EQUALS,
  value: { exact: '', min: null, max: null },
  countToScore: null,
  scoreWeight: null,
  stepType: null,
  step: null,
  unit: null
});

const buildCriteriaAssertion = (criteria: BlockCriterion[]): BlockAssertionModel => ({
  cid: randomUUID(),
  itemCid: null,
  type: BlockAssertionType.CRITERIA,
  valueType: BlockAssertionValueType.INPUT_TEXT_CRITERIA_LIST,
  compareType: BlockAssertionCompareType.AGENT_GRADED,
  value: { exact: null, min: null, max: null, criteria },
  countToScore: true,
  scoreWeight: 100,
  passMessage: null,
  failMessage: null,
  stepType: null,
  step: null,
  unit: null
});

export {
  BlockAssertionCompareType,
  BlockAssertionStepType,
  BlockAssertionType,
  BlockAssertionValueType,
  BlockBlanksDragTypes,
  BlockBooleanType,
  BlockBricksDragTypes,
  BlockCategory,
  BlockCriterionType,
  BlockGroupType,
  BlockGroupUnit,
  BlockIntensity,
  BlockType,
  buildCriteriaAssertion,
  ContentItemModality,
  hasAsyncAssertion,
  isAsyncCompareType,
  makeDefaultAssertionForCard
};
export type {
  BlankContentModel,
  BlankModel,
  BlankOccurrenceModel,
  BlankProblemModel,
  BlockAssertionModel,
  BlockAssertionSpecModel,
  BlockAssertionValueModel,
  BlockBrickCategoryModel,
  BlockBrickModel,
  BlockContentChoiceModel,
  BlockContentModel,
  BlockCreateModel,
  BlockCriterion,
  BlockExerciseModel,
  BlockGenericModel,
  BlockGroupContentModel,
  BlockInputBooleanContentModel,
  BlockInputBricksContentModel,
  BlockInputChoicesContentModel,
  BlockInputDateContentModel,
  BlockInputDatetimeContentModel,
  BlockInputFileUploadContentModel,
  BlockInputFlashcardsContentModel,
  BlockInputFloatContentModel,
  BlockInputIntegerContentModel,
  BlockInputRatingContentModel,
  BlockInputTextContentModel,
  BlockInputTimeContentModel,
  BlockInputTimerContentModel,
  BlockInputTrainingContentModel,
  BlockModel,
  BlockStaticAttachmentModel,
  BlockStaticContentModel,
  BlockUpdateModel,
  CategoryToBricksAssociationsType as BrickAssociationsType,
  ContentItemModel,
  DraggedBlankModel,
  DraggedBrickModel,
  FileUploadIdToMimeTypeAssociationType,
  FlashCardItem,
  TaskCreateModel,
  TaskModel,
  TaskUpdateModel,
  TaskValidationSummaryModel
};
